import {
  differenceInHours,
  differenceInMinutes,
  format,
  getWeek,
  getYear,
  isToday,
  isYesterday,
} from "date-fns";
import { sv as swedish } from "date-fns/locale";
import { BooleanMonthList } from "types/separator/separator.type";

export const dateToYearMonthDay = (date?: string | Date | null | undefined) => {
  if (!date) {
    return "";
  }
  return format(date, "yyyy-MM-dd");
};

export const formatDate = (date?: string | Date | null): string => {
  if (!date) return "-";
  if (isToday(new Date(date))) {
    return "Idag";
  }
  return format(new Date(date), "dd MMMM yyyy", {
    locale: swedish,
  });
};

export const formatDateWithTimeRelative = (date: Date): string => {
  const now = new Date();
  const minutesDiff = differenceInMinutes(now, date);
  const hoursDiff = differenceInHours(now, date);

  // Less than 1 minute ago
  if (minutesDiff < 1) {
    return "Nyss";
  }

  // Less than 1 hour ago
  if (minutesDiff < 60) {
    return `${minutesDiff} minuter sen`;
  }

  // Less than 24 hours ago
  if (hoursDiff < 24) {
    return `${hoursDiff} timmar sedan`;
  }

  // Yesterday
  if (isYesterday(date)) {
    return `Igår ${format(date, "HH:mm")}`;
  }

  // Older than yesterday
  return format(date, "yyyy-MM-dd HH:mm");
};

export const formatDateWithTime = (date?: string | Date): string => {
  if (!date) return "-";
  if (isToday(new Date(date))) {
    return `Idag ${format(new Date(date), "HH:mm", {
      locale: swedish,
    })}`;
  }
  return format(new Date(date), "dd MMMM yyyy HH:mm", {
    locale: swedish,
  });
};

export const getMonthNameWithYear = (date?: string | Date): string => {
  if (!date) return "-";
  const month = format(new Date(date), "MMMM", {
    locale: swedish,
  });

  return `${month.charAt(0).toUpperCase()}${month.slice(1)}${` ${getYear(
    new Date(date),
  )}`}`;
};

export const getWeekWithLabel = (date: Date) =>
  `Vecka ${getWeek(date, {
    locale: swedish,
  })}`;

export const stringToDate = (str?: string) =>
  !str ? undefined : new Date(str);

export function getMonthsString(reminderMonths?: BooleanMonthList): string {
  if (!reminderMonths) return "";
  const monthsEnglish: (keyof BooleanMonthList)[] = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  const monthsSwedish: string[] = [];

  monthsEnglish.forEach((month, index) => {
    if (reminderMonths[month]) {
      // Add 1 to index as Date takes month as 0 based index
      const date = new Date(2023, index, 1);
      const monthSwedish = format(date, "MMMM", { locale: swedish });
      const monthSwedishCapitalized =
        monthSwedish.charAt(0).toUpperCase() + monthSwedish.slice(1);
      monthsSwedish.push(monthSwedishCapitalized);
    }
  });

  return monthsSwedish.join(", ");
}

export const svenskMånad = (date: Date): string => {
  if (!date) return "-";
  return format(date, "MMMM", { locale: swedish })
    .replace("January", "januari")
    .replace("February", "februari")
    .replace("March", "mars")
    .replace("April", "april")
    .replace("May", "maj")
    .replace("June", "juni")
    .replace("July", "juli")
    .replace("August", "augusti")
    .replace("September", "september")
    .replace("October", "oktober")
    .replace("November", "november")
    .replace("December", "december");
};

export const svenskMånadKort = (date: Date): string => {
  if (!date) return "-";
  return format(date, "MMMM", { locale: swedish })
    .replace("January", "jan")
    .replace("February", "feb")
    .replace("March", "mar")
    .replace("April", "apr")
    .replace("May", "maj")
    .replace("June", "jun")
    .replace("July", "jul")
    .replace("August", "aug")
    .replace("September", "sep")
    .replace("October", "okt")
    .replace("November", "nov")
    .replace("December", "dec");
};
